import type React from 'react'
import { Fragment } from 'react'
import { Stack, Alert, Divider, Box } from '@mui/material'
import { Favorite } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

import CartItemList from '../CartItem/CartItemList'
import AllergensInfo from '../../shared/AllergensInfo'
import SubtotalLine from './SubtotalLine'
import type { CanteenCartItem } from '../types'
import type { FragmentType } from '@/generated/gql'
import { graphql, useFragment } from '@/generated/gql'
import useCharityMeals from '@/helpers/features/charityMeals'
import { useLocale } from '@/components/Link'
import useTenant from '@/hooks/useTenant'
import { useDefaultCurrency } from '../../state/useDefaultCurrency'

const canteenCartDetailsMenuDataFragment = graphql(`
  fragment CanteenCartDetails_MenuDataFragment on CCMealService {
    availableSubsidy
  }
`)

const canteenCartDetailsUserDataFragment = graphql(`
  fragment CanteenCartDetails_UserDataFragment on User {
    subsidySettings {
      unlimitedSubsidy
    }
    account {
      topUpEnabled
      userInterfaceSettings {
        hideCCPrices
      }
    }
    wallet {
      balance
    }
  }
`)

type Props = {
  menuData: FragmentType<typeof canteenCartDetailsMenuDataFragment>
  userData: FragmentType<typeof canteenCartDetailsUserDataFragment>
  error: string | null
  onDismissError: () => void
  onUpdate: (item: CanteenCartItem) => void
  onRemove: (itemReference: string) => void
  cartItems: CanteenCartItem[]
  cartTotal: number
}

const Details: React.FC<Props> = ({
  menuData: menuDataProp,
  userData: userDataProp,
  cartItems,
  onUpdate,
  onRemove,
  onDismissError,
  error,
  cartTotal,
}) => {
  const locale = useLocale()
  const tenant = useTenant()
  const currency = useDefaultCurrency()

  const menuData = useFragment(canteenCartDetailsMenuDataFragment, menuDataProp)
  const userData = useFragment(canteenCartDetailsUserDataFragment, userDataProp)

  const charityMeals = useCharityMeals()
  const { t } = useTranslation()

  const mainCount = cartItems.reduce((acc, cartItem) => {
    return acc + (cartItem.item.type === 'main' ? cartItem.qty : 0)
  }, 0)

  const itemCount = cartItems.reduce((acc, cartItem) => acc + cartItem.qty, 0)
  const subtotal = cartTotal
  const amountSubsidised = Math.min(subtotal, menuData.availableSubsidy)
  const amountToPay = subtotal - amountSubsidised
  const creditsToUse = Math.min(amountToPay, userData.wallet.balance)

  const topUpEnabled = Boolean(userData.account?.topUpEnabled)
  const unlimitedSubsidy = Boolean(userData.subsidySettings.unlimitedSubsidy)
  const insufficientCredits =
    !unlimitedSubsidy &&
    subtotal > Number(menuData.availableSubsidy) + Number(userData.wallet.balance)

  const shouldShowSubsidyAlert = !topUpEnabled && insufficientCredits
  const hidePrices = Boolean(userData.account?.userInterfaceSettings.hideCCPrices)

  const defaultLocale = tenant.defaultLocale

  const { format } = Intl.NumberFormat(locale || defaultLocale, {
    style: 'currency',
    currencySign: 'standard',
    currency,
  })

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          position: 'relative',
          overflow: 'scroll',
        }}
      >
        {cartItems.length > 0 ? (
          <>
            <Stack spacing={2} paddingY={2} direction="column" paddingX={3}>
              {error && (
                <Alert severity="error" onClose={() => onDismissError()}>
                  {error}
                </Alert>
              )}

              <CartItemList
                onRemove={onRemove}
                hidePrices={hidePrices}
                cartItems={cartItems}
                onUpdate={onUpdate}
              />

              <AllergensInfo />
            </Stack>

            <Divider />

            <Stack direction="column" spacing={1} paddingX={3} paddingY={2}>
              {!hidePrices ? (
                <SubtotalLine left={`Subtotal (${itemCount} items)`} right={format(subtotal)} />
              ) : null}

              {unlimitedSubsidy && !hidePrices ? (
                <SubtotalLine
                  left="Unlimited Subsidy"
                  right={`- ${format(subtotal)}`}
                  color="success.main"
                />
              ) : (
                <Fragment>
                  {amountSubsidised > 0 && !hidePrices ? (
                    <SubtotalLine
                      left={t('cc:freeCredits')}
                      right={`- ${format(amountSubsidised)}`}
                      color="success.main"
                    />
                  ) : null}

                  {creditsToUse > 0 && !hidePrices && topUpEnabled ? (
                    <SubtotalLine left="Personal Credits" right={`- ${format(creditsToUse)}`} />
                  ) : null}

                  {!hidePrices ? <SubtotalLine left={t('delivery')} right={t('free')} /> : null}
                </Fragment>
              )}
              {mainCount > 0 && charityMeals.enabled && (
                <SubtotalLine
                  left={t('cc:mealsToChild', { count: mainCount })}
                  right={<Favorite color="info" fontSize="small" />}
                  color="info.main"
                />
              )}
            </Stack>
          </>
        ) : null}
      </Box>

      {shouldShowSubsidyAlert && (
        <Alert severity="info" sx={{ alignItems: 'center', margin: '1rem' }}>
          You do not have enough subsidised credits. Please edit your basket.
        </Alert>
      )}
    </>
  )
}

export default Details
